import _ from 'lodash';
import angular from 'angular';
import template from './template.html';
import { PRIORITY_VALUES } from './item';
import './style.scss';

const splitSkillsByPriority = (skills) => {
    const filterPrioritySkills = [];
    const restPrioritySkills = [];

    (skills || []).forEach((skill) => {
        if (skill.priorityValue === PRIORITY_VALUES.FILTER) {
            filterPrioritySkills.push(skill);
        } else {
            restPrioritySkills.push(skill);
        }
    });

    return { filterPrioritySkills, restPrioritySkills };
};

const getSkillsFitToFreeSpace = (skills, freeSpace) => {
    let index = 0;
    let currentSpace = 0;

    while (currentSpace <= freeSpace && index < skills.length) {
        currentSpace += skills[index].width;
        // eslint-disable-next-line no-plusplus
        index++;
    }

    if (currentSpace <= freeSpace) {
        // eslint-disable-next-line no-plusplus
        index++;
    }

    return {
        skillsFitsToFreeSpace: skills.slice(0, index - 1),
        restSkills: skills.slice(index - 1),
    };
};

const getLinesFit = (skillsWithWidth, containerWidth) => {
    let lines = 1;
    let freeSpace = containerWidth;
    let currentIndex = 0;

    while (currentIndex < skillsWithWidth.length) {
        const skillWidth = skillsWithWidth[currentIndex].width;

        if (freeSpace - skillWidth < 0) {
            // eslint-disable-next-line no-plusplus
            lines++;
            freeSpace = containerWidth - skillWidth;
        } else {
            freeSpace -= skillWidth;
        }

        // eslint-disable-next-line no-plusplus
        currentIndex++;
    }

    return {
        lines,
        freeSpace,
    };
};

const getAlwaysShownSkills = _.memoize((skillsWithWidth, containerWidth, alwaysLinesToShow) => {
    const { filterPrioritySkills, restPrioritySkills } = splitSkillsByPriority(skillsWithWidth);
    const { lines, freeSpace } = getLinesFit(filterPrioritySkills, containerWidth);
    let { skillsFitsToFreeSpace, restSkills } = getSkillsFitToFreeSpace(restPrioritySkills, freeSpace);
    let currentLines = lines;

    if (currentLines < alwaysLinesToShow) {
        while (currentLines < alwaysLinesToShow) {
            const {
                skillsFitsToFreeSpace: skillsFitsToFreeSpaceNextLine,
                restSkills: nextRestSkills,
            } = getSkillsFitToFreeSpace(restSkills, containerWidth);

            skillsFitsToFreeSpace = [...skillsFitsToFreeSpace, ...skillsFitsToFreeSpaceNextLine];
            restSkills = nextRestSkills;

            // eslint-disable-next-line no-plusplus
            currentLines++;
        }
    }

    return [...filterPrioritySkills, ...skillsFitsToFreeSpace];
});

export const loadingSkillsCount = 50;
export const loadingSkillNormalWidth = 70;

export class MatchedSkillsList {
    defaultLabelKey = 'SKILLS';
    skills = [];
    expanded = null;
    skillsWithWidth = [];
    skillsToPresent = [];
    isLoading = false;
    // generate skills for preview while loading original skills with different width (random used to shake width, to be more natural)
    loadingSkillsWidth = Array.from({ length: loadingSkillsCount }).map(
        (item, index) => (Math.random() * Math.floor(index) + loadingSkillNormalWidth),
    );

    /* @ngInject */
    constructor($scope, $filter, $element, $timeout, $window) {
        this.$scope = $scope;
        this.$filter = $filter;
        this.$element = $element[0];
        this.$timeout = $timeout;
        this.$window = $window;
    }

    $postLink() {
        angular.element(this.$window).on('resize', () => {
            this.recalculateSkills();
            this.$scope.$apply();
        });

        this.$timeout(() => {
            this.skillsWithWidth = this.getSkillsWidth();
            this.recalculateSkills();
        }, 0);
    }

    getSkillsWidth() {
        const skills = _.get(this.$element, 'children.0.children.1.children.0.children');
        const skillsWidth = [...skills].map(skill => skill.offsetWidth);

        return (this.skills || []).map((skill, index) => ({ ...skill, width: skillsWidth[index] }));
    }

    $onChanges(changes) {
        this.labelKey = this.labelKey || this.defaultLabelKey;
        const isLoadingPrevValue = _.get(changes, 'isLoading.previousValue');
        const isLoadingCurrValue = _.get(changes, 'isLoading.currentValue');

        if (isLoadingPrevValue && !isLoadingCurrValue) {
            this.$timeout(() => {
                this.skillsWithWidth = this.getSkillsWidth();
                this.recalculateSkills();
            }, 0);
        }

        this.recalculateSkills();
    }

    $onDestroy() {
        angular.element(this.$window).off('resize');
    }

    toggleExpanded() {
        this.expanded = !this.expanded;

        this.recalculateSkills();
    }

    recalculateSkills() {
        const containerWidth = _.get(this, '$element.children.0.children.1.children.0.offsetWidth', 0);
        const canShowAllSkills = !containerWidth || this.expanded || this.isLoading;

        if (canShowAllSkills) {
            this.setSkillsToPresent(this.skills);

            return;
        }

        if (this.skillsWithWidth.length === this.skills.length) {
            const alwaysShownSkills = getAlwaysShownSkills(this.skillsWithWidth, containerWidth, 2);

            this.setSkillsToPresent(alwaysShownSkills);

            if (alwaysShownSkills.length < this.skills.length) {
                this.collapse();
            } else {
                this.flushState();
            }
        } else {
            this.setSkillsToPresent(this.skills);
        }
    }

    setSkillsToPresent(skillsToPresent = []) {
        if (this.skillsToPresent === skillsToPresent) {
            return;
        }

        this.skillsToPresent = skillsToPresent;

        this.recalculateExpandLinkText();
    }

    recalculateExpandLinkText() {
        const overflowCount = _.get(this.skills, 'length', 0) - _.get(this.skillsToPresent, 'length', 0);

        this.showButtons = overflowCount > 0 || this.expanded !== null;
        this.expandLinkText = this.expanded ? this.$filter('translate')('Less') : `${overflowCount} ${this.$filter('translate')('MORE')}`;
    }

    expand() {
        this.expanded = true;
    }

    collapse() {
        this.expanded = false;
    }

    flushState() {
        this.expanded = null;
    }
}

export const MatchedSkillsListConfiguration = {
    template,
    bindings: {
        skills: '<',
        labelKey: '<',
        isLoading: '<',
        hideCheckmark: '<',
        isFullListView: '<',
    },
    controller: MatchedSkillsList,
    controllerAs: 'matchedSkillsListCtrl',
};

angular.module('TALENYA-FE').component('matchedSkillsList', MatchedSkillsListConfiguration);
